import { FC, useCallback, useMemo } from 'react';
import OnOffList from '../on-off-list';
import { FormProps } from '../helpers';
import { UiOption } from '@/lib/helpers';
import {
  getSensitiveDataTypeOptions,
  SensitiveDataCategoryOptions,
  SensitiveDataEntity,
} from '@/lib/models/policy-model/sensitive-data';
import type { OnOffEntity } from '../on-off-list/helpers';
import useUserControl from '@/hooks/use-user-control.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import useDrawer from '@/hooks/use-drawer.hook';

interface Props extends FormProps {
  value: SensitiveDataEntity[] | undefined;
  onChange: (entities: SensitiveDataEntity[] | undefined) => void;
  policy: PolicyModel;
}

const SdEntityList: FC<Props> = ({ value: entities, onChange, policy, ...rest }) => {
  const { openDrawer } = useDrawer();
  const { checkLockedAndEmit } = useUserControl();

  const { isDefault: isDefaultPolicy } = policy;

  const onOffValue = useMemo((): OnOffEntity<'type'>[] | undefined => {
    if (!entities?.length) {
      return undefined;
    }
    return entities.map(({ type, enabled }) => ({
      type,
      action: enabled ? 'allow' : 'block',
    }));
  }, [entities]);

  const handleOnOffChange = useCallback(
    (onOffEntities: OnOffEntity<'type'>[] | undefined) => {
      if (!onOffEntities?.length) {
        onChange(undefined);
        return;
      }
      const scanTypeMap = new Map((entities ?? []).map((entity) => [entity.type, entity.scanType]));
      const result = onOffEntities.map(({ type, action }) => ({
        type,
        scanType: scanTypeMap.get(type) || 1,
        enabled: action === 'allow',
      }));
      onChange(result);
    },
    [entities, onChange]
  );

  const options = useMemo(() => {
    return getSensitiveDataTypeOptions(policy);
  }, [policy]);

  const handleOpenDrawer = (opt: UiOption) => {
    if (checkLockedAndEmit()) {
      return;
    }
    const { value } = opt;
    openDrawer('sd-entity', {
      id: value,
    });
  };

  return (
    <OnOffList
      {...rest}
      value={onOffValue}
      onChange={handleOnOffChange}
      options={options}
      idField="type"
      categoryOptions={SensitiveDataCategoryOptions}
      height={400}
      search
      onSettings={handleOpenDrawer}
      defaultValue={isDefaultPolicy ? false : undefined}
      withNotSet={!isDefaultPolicy}
    />
  );
};

export default SdEntityList;
